import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authApi } from '@/api/authApi';
import {
  getAccessToken,
  setAccessToken,
  setRefreshToken,
  removeTokens,
} from '@/utils/storage';

interface User {
  email: string;
  name?: string;
  full_name?: string;
  picture?: string;
  gender?: string;
  age?: number;
  phone?: string;
  profile_complete?: boolean;
  referral_code?: string;
  has_made_payment?: boolean;
  /** Decided by the API from its own configuration; never from a value in this bundle. */
  is_admin?: boolean;
  [key: string]: unknown;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  needsProfileCompletion: boolean;
  termsAccepted: boolean;
  /** `known` is what the login response already said about the person: the
   *  fallback if the profile fetch that follows fails, so a successful login
   *  never leaves them stranded on the login page as "not signed in". */
  signIn: (access: string, refresh?: string, known?: Partial<User> | null) => Promise<void>;
  signOut: () => void;
  refreshUser: () => Promise<void>;
  acceptTerms: () => Promise<void>;
  setNeedsProfileCompletion: (val: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  isAdmin: false,
  needsProfileCompletion: false,
  termsAccepted: false,
  signIn: async () => {},
  signOut: () => {},
  refreshUser: async () => {},
  acceptTerms: async () => {},
  setNeedsProfileCompletion: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [termsAccepted, setTermsAcceptedState] = useState(true);

  const checkProfileComplete = (userData: Record<string, unknown>) => {
    const userInfo = (userData?.user || userData) as Record<string, unknown>;
    const profileComplete = userInfo?.profile_complete || (
      userInfo?.gender !== null && userInfo?.gender !== undefined
    );
    setNeedsProfileCompletion(!profileComplete);
    // terms_accepted defaults to true for existing users who don't have the field yet
    setTermsAcceptedState(userInfo?.terms_accepted !== false);
  };

  useEffect(() => {
    const restore = async () => {
      try {
        const access = getAccessToken();
        if (access) {
          const res = await authApi.getProfile();
          const userData = res.data || res;
          setUser(userData?.user || userData);
          checkProfileComplete(userData);
        }
      } catch {
        // Token invalid, silently fail
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, []);

  const signIn = useCallback(async (access: string, refresh?: string, known?: Partial<User> | null) => {
    setAccessToken(access);
    if (refresh) setRefreshToken(refresh);
    try {
      const res = await authApi.getProfile();
      const userData = res.data || res;
      setUser(userData?.user || userData);
      checkProfileComplete(userData);
    } catch {
      // The tokens are stored, so the person IS signed in. Use what the login
      // response told us and let the next profile fetch fill in the rest. The
      // profile-completion gate is decided from the same data, so a fallback
      // sign-in cannot skip it.
      if (known?.email) {
        setUser(known as User);
        checkProfileComplete({ user: known });
      }
    }
  }, []);

  const signOut = useCallback(() => {
    removeTokens();
    setUser(null);
    setNeedsProfileCompletion(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authApi.getProfile();
      const userData = res.data || res;
      setUser(userData?.user || userData);
      checkProfileComplete(userData);
    } catch {
      // Refresh failed
    }
  }, []);

  const acceptTerms = useCallback(async () => {
    await authApi.acceptTerms();
    setTermsAcceptedState(true);
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = user?.is_admin === true;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        isAdmin,
        needsProfileCompletion,
        termsAccepted,
        signIn,
        signOut,
        refreshUser,
        acceptTerms,
        setNeedsProfileCompletion,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
